import { Icon16ChainOutline, Icon20GlobeOutline, Icon28PictureOutline } from '@vkontakte/icons';
import classNames from 'clsx';
import React, { memo, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { seoEnable } from 'reactApp/appHelpers/featuresHelpers/features/seoEnable';
import { getFeatureCreatePublicAlbum } from 'reactApp/modules/features/features.selectors';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { AlbumLabel } from 'reactApp/ui/AlbumLabel/AlbumLabel';
import { DataListCheckbox } from 'reactApp/ui/DataListCheckbox/DataListCheckbox';
import { Link } from 'reactApp/ui/Link/Link';
import { Icon20Vigbo } from 'reactApp/ui/VKUIIcons';

import styles from './Album.css';
import type { IProps } from './Album.types';
import { useLoading } from './Albums.hooks';
import { Options } from './Options';

export const Album = memo(function Album(props: IProps) {
    const {
        thumbnails,
        name,
        id,
        weblink,
        onClick,
        isLink = true,
        isSelectable = false,
        isSelected = false,
        weblinkSeoEnabled,
        href,
        storage,
    } = props;
    const [isLoaded, onLoad] = useLoading();

    const isVigbo = storage === EStorageType.vigbo;

    const isPublicAlbumEnabled = useSelector(getFeatureCreatePublicAlbum);
    const showWeblink = weblink && isPublicAlbumEnabled;

    const icon = useMemo(() => {
        if (isVigbo) {
            return <Icon20Vigbo width={16} height={16} className={styles.labelIcon} />;
        }

        if (showWeblink) {
            return weblinkSeoEnabled && seoEnable ? (
                <Icon20GlobeOutline width={16} height={16} className={classNames(styles.labelIcon, styles.seo)} />
            ) : (
                <Icon16ChainOutline display="inline" className={classNames(styles.labelIcon, styles.weblink)} />
            );
        }
    }, [showWeblink, weblinkSeoEnabled, isVigbo]);

    return (
        <Link handleClick={onClick} noLink={!isLink} handleGoTo={onClick} storage={storage} id={id} className={styles.link} href={href}>
            <div
                className={classNames({
                    [styles.root]: true,
                    [styles.root_selected]: isSelected,
                    [styles.root_border]: Boolean(thumbnails),
                })}
                data-qa-id={id}
                data-qa-album={name}
            >
                {!thumbnails ? (
                    <Icon28PictureOutline className={styles.icon} data-qa-id="album-icon" />
                ) : (
                    <img
                        src={thumbnails['336x304']}
                        srcSet={thumbnails['1000x1000']}
                        loading="lazy"
                        className={classNames(styles.image, { [styles.image_loaded]: isLoaded })}
                        onLoad={onLoad}
                        data-qa-id="album-thumbnail"
                    />
                )}
                <AlbumLabel className={styles.title} qaLabel="title">
                    <>
                        {icon}
                        {name}
                    </>
                </AlbumLabel>

                {!isSelectable && <Options id={id} storage={storage} />}
                {isSelectable && (
                    <div className={styles.checkbox}>
                        <DataListCheckbox checked={isSelected} />
                    </div>
                )}
            </div>
        </Link>
    );
});
